"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { MarkdownRenderer } from "@/components/chat/MarkdownRenderer";

interface Note {
  id: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
}

interface PanelSize {
  width: number;
  height: number;
}

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "";
const STORAGE_KEY_DESKTOP = "superone_notes_panel_size_desktop";
const STORAGE_KEY_MOBILE = "superone_notes_panel_size_mobile";
const STORAGE_KEY_LIST_COLLAPSED = "superone_notes_list_collapsed";
const MIN_WIDTH = 400;
const MIN_HEIGHT = 300;
const MAX_WIDTH_RATIO = 0.8;
const MAX_HEIGHT_RATIO = 0.85;

export function NotesPanel({ onClose }: { onClose: () => void }) {
  const { token } = useAuth();
  const [notes, setNotes] = useState<Note[]>([]);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false); // 左侧列表折叠状态
  const [isEditing, setIsEditing] = useState(false); // 编辑模式 vs 预览模式

  // 跟踪内容是否有变化
  const hasChanges = selectedNote
    ? (editTitle !== selectedNote.title || editContent !== selectedNote.content)
    : (editTitle.trim() !== "" || editContent.trim() !== "");

  // 面板大小和拖拽
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelSize, setPanelSize] = useState<PanelSize>({ width: 600, height: 500 });
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

  // 检测是否是移动端 - 初始值基于 SSR 安全检测
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < 768;
    }
    return false;
  });

  // 检测移动端
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // 加载保存的列表折叠状态
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY_LIST_COLLAPSED);
    if (saved) {
      setListCollapsed(saved === "true");
    } else if (isMobile) {
      // 移动端默认折叠列表
      setListCollapsed(true);
    }
  }, [isMobile]);

  // 保存列表折叠状态
  const toggleListCollapsed = () => {
    const newState = !listCollapsed;
    setListCollapsed(newState);
    localStorage.setItem(STORAGE_KEY_LIST_COLLAPSED, String(newState));
  };

  // 加载保存的面板大小
  useEffect(() => {
    const storageKey = isMobile ? STORAGE_KEY_MOBILE : STORAGE_KEY_DESKTOP;
    const saved = localStorage.getItem(storageKey);
    if (saved && !isMobile) {
      try {
        const size = JSON.parse(saved);
        // 确保不超过当前屏幕
        const maxWidth = window.innerWidth * MAX_WIDTH_RATIO;
        const maxHeight = window.innerHeight * MAX_HEIGHT_RATIO;
        setPanelSize({
          width: Math.min(Math.max(size.width, MIN_WIDTH), maxWidth),
          height: Math.min(Math.max(size.height, MIN_HEIGHT), maxHeight)
        });
      } catch {
        // ignore
      }
    } else if (isMobile) {
      // 移动端全屏显示
      setPanelSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    }
  }, [isMobile]);

  // 保存面板大小
  const savePanelSize = useCallback((size: PanelSize) => {
    const storageKey = isMobile ? STORAGE_KEY_MOBILE : STORAGE_KEY_DESKTOP;
    localStorage.setItem(storageKey, JSON.stringify(size));
  }, [isMobile]);

  // 拖拽调整大小
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: panelSize.width,
      height: panelSize.height
    };
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStartRef.current.x;
      const deltaY = e.clientY - resizeStartRef.current.y;

      const maxWidth = window.innerWidth * MAX_WIDTH_RATIO;
      const maxHeight = window.innerHeight * MAX_HEIGHT_RATIO;

      const newWidth = Math.min(Math.max(resizeStartRef.current.width + deltaX, MIN_WIDTH), maxWidth);
      const newHeight = Math.min(Math.max(resizeStartRef.current.height + deltaY, MIN_HEIGHT), maxHeight);

      setPanelSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      savePanelSize(panelSize);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, panelSize, savePanelSize]);

  // 加载笔记列表
  const loadNotes = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotes(data.notes || []);
      }
    } catch (error) {
      console.error("Failed to load notes:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  // 创建新笔记
  const handleCreate = async () => {
    if (!token || !editContent.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/notes`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          title: editTitle.trim() || null,
          content: editContent.trim()
        })
      });
      if (res.ok) {
        const data = await res.json();
        setNotes([data.note, ...notes]);
        setSelectedNote(data.note);
        setIsCreating(false);
        setIsEditing(false); // 保存后进入预览模式
        setEditTitle(data.note.title);
        setEditContent(data.note.content);
      }
    } catch (error) {
      console.error("Failed to create note:", error);
    } finally {
      setSaving(false);
    }
  };

  // 更新笔记
  const handleUpdate = async () => {
    if (!token || !selectedNote) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/notes/${selectedNote.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          title: editTitle.trim() || null,
          content: editContent.trim()
        })
      });
      if (res.ok) {
        const data = await res.json();
        setNotes(notes.map(n => n.id === selectedNote.id ? data.note : n));
        setSelectedNote(data.note);
        setEditTitle(data.note.title);
        setEditContent(data.note.content);
        setIsEditing(false); // 保存后进入预览模式
      }
    } catch (error) {
      console.error("Failed to update note:", error);
    } finally {
      setSaving(false);
    }
  };

  // 删除笔记
  const handleDelete = async (noteId: string) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/notes/${noteId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setNotes(notes.filter(n => n.id !== noteId));
        if (selectedNote?.id === noteId) {
          setSelectedNote(null);
          setIsEditing(false);
        }
      }
    } catch (error) {
      console.error("Failed to delete note:", error);
    }
  };

  // 复制内容
  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  };

  // 开始创建
  const startCreate = () => {
    setSelectedNote(null);
    setEditTitle("");
    setEditContent("");
    setIsCreating(true);
    setIsEditing(true); // 创建时直接进入编辑模式
  };

  // 取消编辑/创建
  const cancelEdit = () => {
    if (isCreating) {
      setIsCreating(false);
      setEditTitle("");
      setEditContent("");
      setSelectedNote(null);
      setIsEditing(false);
    } else if (selectedNote) {
      // 恢复原始内容并退出编辑模式
      setEditTitle(selectedNote.title);
      setEditContent(selectedNote.content);
      setIsEditing(false);
    }
  };

  // 选择笔记 - 进入预览模式
  const selectNote = (note: Note) => {
    setSelectedNote(note);
    setEditTitle(note.title);
    setEditContent(note.content);
    setIsCreating(false);
    setIsEditing(false); // 选择笔记时进入预览模式
  };

  // 切换编辑/预览模式
  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };

  return (
    <>
      {/* Backdrop - 移动端使用更高的 z-index */}
      <div
        className={`fixed inset-0 bg-black/20 ${isMobile ? "z-[60]" : "z-40"}`}
        onClick={onClose}
      />
      {/* Panel - 移动端全屏且更高 z-index */}
      <div
        ref={panelRef}
        className={`fixed shadow-2xl flex flex-col overflow-hidden select-none ${
          isMobile
            ? "inset-0 z-[61]"
            : "left-20 bottom-16 rounded-lg z-50"
        }`}
        style={isMobile ? {
          backgroundColor: "#1e1e1e"
        } : {
          backgroundColor: "#1e1e1e",
          border: "1px solid #3a3a3a",
          width: panelSize.width,
          height: panelSize.height
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#3a3a3a] flex-shrink-0">
          <div className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="#f97316"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            <span className="text-[#e5e5e5] font-medium">快速笔记</span>
            <span className="text-xs text-[#606060]">({notes.length})</span>
          </div>
          <div className="flex items-center gap-1">
            {/* 折叠/展开列表按钮 */}
            <button
              onClick={toggleListCollapsed}
              className="p-1.5 rounded hover:bg-[#3a3a3a] transition-colors text-[#808080] hover:text-[#e5e5e5]"
              title={listCollapsed ? "展开列表" : "折叠列表"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 transition-transform ${listCollapsed ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              onClick={startCreate}
              className="p-1.5 rounded hover:bg-[#3a3a3a] transition-colors text-[#808080] hover:text-[#e5e5e5]"
              title="新建笔记"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-[#3a3a3a] transition-colors text-[#808080] hover:text-[#e5e5e5]"
              title="关闭"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content - 左右分栏 */}
        <div className="flex-1 flex overflow-hidden">
          {/* 左侧 - 笔记列表 (可折叠) */}
          <div
            className={`flex-shrink-0 border-r border-[#2a2a2a] overflow-y-auto transition-all duration-300 ${
              listCollapsed ? "w-0 border-r-0 overflow-hidden" : "w-48"
            }`}
          >
            {loading ? (
              <div className="p-4 text-center text-[#606060] text-sm whitespace-nowrap">加载中...</div>
            ) : notes.length === 0 && !isCreating ? (
              <div className="p-4 text-center text-[#606060] text-sm whitespace-nowrap">
                <p>暂无笔记</p>
                <button
                  onClick={startCreate}
                  className="mt-2 text-[#f97316] hover:text-[#fb923c]"
                >
                  创建第一条
                </button>
              </div>
            ) : (
              <div className="py-1">
                {notes.map(note => (
                  <div
                    key={note.id}
                    onClick={() => {
                      selectNote(note);
                      // 移动端选择后自动折叠列表
                      if (isMobile) setListCollapsed(true);
                    }}
                    className={`px-3 py-2 cursor-pointer transition-colors border-l-2 whitespace-nowrap ${
                      selectedNote?.id === note.id
                        ? "bg-[#2a2a2a] border-[#a855f7]"
                        : "border-transparent hover:bg-[#252525]"
                    }`}
                  >
                    <div className="text-sm text-[#e5e5e5] truncate">
                      {note.title || "无标题"}
                    </div>
                    <div className="text-xs text-[#606060] truncate mt-0.5">
                      {note.content.substring(0, 50)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 右侧 - 详情/编辑 (始终可编辑) */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {(selectedNote || isCreating) ? (
              // 编辑模式 - 选中笔记或创建新笔记时
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* 顶部工具栏 */}
                <div className="px-4 py-2 border-b border-[#2a2a2a] flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isCreating ? (
                      <span className="text-xs text-[#f97316]">新建笔记</span>
                    ) : (
                      <span className="text-xs text-[#606060] truncate">
                        更新于 {new Date(selectedNote!.updated_at).toLocaleString("zh-CN")}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    {/* 编辑/预览切换按钮 - 仅在已有笔记时显示 */}
                    {!isCreating && selectedNote && (
                      <button
                        onClick={toggleEditMode}
                        className={`p-1.5 rounded transition-colors ${
                          isEditing
                            ? "bg-[#f97316]/20 text-[#f97316]"
                            : "text-[#808080] hover:bg-[#3a3a3a] hover:text-[#e5e5e5]"
                        }`}
                        title={isEditing ? "预览" : "编辑"}
                      >
                        {isEditing ? (
                          // 预览图标 (眼睛)
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        ) : (
                          // 编辑图标 (铅笔)
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        )}
                      </button>
                    )}
                    {/* 复制按钮 */}
                    <button
                      onClick={() => handleCopy(editContent)}
                      className="p-1.5 rounded hover:bg-[#3a3a3a] transition-colors text-[#808080] hover:text-[#e5e5e5]"
                      title="复制内容"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                    {/* 删除按钮 - 仅在编辑已有笔记时显示 */}
                    {!isCreating && selectedNote && (
                      <button
                        onClick={() => handleDelete(selectedNote.id)}
                        className="p-1.5 rounded hover:bg-[#3a3a3a] transition-colors text-[#808080] hover:text-red-400"
                        title="删除"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* 内容区域 - 编辑模式或预览模式 */}
                <div className="flex-1 flex flex-col p-4 overflow-hidden">
                  {isEditing || isCreating ? (
                    // 编辑模式
                    <>
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        placeholder="标题 (可选，留空自动生成)"
                        className="w-full px-3 py-2 text-sm rounded bg-[#2a2a2a] text-[#e5e5e5] border border-[#3a3a3a] outline-none focus:border-[#f97316] mb-3 flex-shrink-0"
                      />
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        placeholder="内容... (支持 Markdown 格式)"
                        className="flex-1 w-full px-3 py-2 text-sm rounded bg-[#2a2a2a] text-[#e5e5e5] border border-[#3a3a3a] outline-none focus:border-[#f97316] resize-none font-mono"
                      />
                    </>
                  ) : (
                    // 预览模式 - Markdown 渲染
                    <div className="flex-1 overflow-y-auto">
                      {editTitle && (
                        <h2 className="text-lg font-bold text-[#e5e5e5] mb-3 pb-2 border-b border-[#3a3a3a]">
                          {editTitle}
                        </h2>
                      )}
                      <div className="text-sm">
                        <MarkdownRenderer content={editContent} />
                      </div>
                    </div>
                  )}
                </div>

                {/* 底部操作栏 - 编辑模式下有变化时显示保存按钮 */}
                {(isEditing || isCreating) && (hasChanges || isCreating) && (
                  <div className="flex gap-2 px-4 pb-4 flex-shrink-0">
                    <button
                      onClick={isCreating ? handleCreate : handleUpdate}
                      disabled={saving || !editContent.trim()}
                      className="flex-1 px-4 py-2 text-sm bg-[#f97316] hover:bg-[#ea580c] text-white rounded transition-colors disabled:opacity-50"
                    >
                      {saving ? "保存中..." : "保存"}
                    </button>
                    <button
                      onClick={cancelEdit}
                      disabled={saving}
                      className="px-4 py-2 text-sm bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#e5e5e5] rounded transition-colors"
                    >
                      {isCreating ? "取消" : "撤销修改"}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // 空状态
              <div className="flex-1 flex items-center justify-center text-[#606060]">
                <div className="text-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 mx-auto mb-3 opacity-50"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p className="text-sm">选择一个笔记查看</p>
                  <p className="text-xs mt-1">或点击 + 创建新笔记</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 拖拽调整大小的手柄 - 仅桌面端显示 */}
        {!isMobile && (
          <div
            className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
            onMouseDown={handleResizeStart}
            style={{
              background: "linear-gradient(135deg, transparent 50%, #4a4a4a 50%)"
            }}
          />
        )}
      </div>
    </>
  );
}
